import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, Users, Award, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/swcybtk6_WhatsApp%20Image%202026-03-03%20at%2012.01.34%20PM.jpeg";

const values = [
  {
    icon: Heart,
    title: "Quality First",
    description: "We never compromise on the quality of our produce. Every product that leaves our farm meets the highest standards."
  },
  {
    icon: Leaf,
    title: "Sustainability",
    description: "Our farming practices prioritize environmental stewardship and sustainable agriculture for future generations."
  },
  {
    icon: Users,
    title: "Community",
    description: "We believe in empowering our local community through employment, training, and fair trade practices."
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Continuous improvement and innovation drive us to be the best in agricultural production."
  }
];

const team = [
  {
    name: "AfriGrow Management",
    role: "Leadership Team",
    description: "Dedicated professionals with decades of agricultural experience leading our operations."
  },
  {
    name: "Farm Workers",
    role: "Production Team",
    description: "Skilled farmers and animal handlers ensuring quality daily operations."
  },
  {
    name: "Support Staff",
    role: "Customer Service",
    description: "Friendly team ready to assist with orders, inquiries, and farm visits."
  }
];

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="about-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Growing Excellence Since Day One
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              AfriGrow Farm is a premier agricultural service provider located in Igbodu, Lagos, Nigeria. 
              We specialize in sustainable livestock and crop production.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white" data-testid="story-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={LOGO_URL}
                alt="AfriGrow Farm" 
                className="w-full h-[500px] object-contain bg-stone-50 rounded-2xl p-8"
              />
            </div>
            
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
                Our Story
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900 mb-6">
                From Humble Beginnings to Agricultural Excellence
              </h2>
              <div className="space-y-4 text-stone-600 text-lg leading-relaxed">
                <p>
                  AfriGrow Farm was founded with a simple vision: to provide quality agricultural products 
                  while promoting sustainable farming practices in Nigeria.
                </p>
                <p>
                  What started as a small family farm has grown into a comprehensive agricultural operation 
                  spanning pig farming, fish farming, grasscutter breeding, vegetable production, and palm cultivation.
                </p>
                <p>
                  Today, we serve hundreds of customers across Lagos and beyond, maintaining our commitment 
                  to quality, sustainability, and customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-stone-100" data-testid="mission-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-10 rounded-2xl shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-green-100 rounded-xl">
                  <Target className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-stone-900">Our Mission</h3>
              </div>
              <p className="text-stone-600 text-lg leading-relaxed">
                To provide high-quality, sustainably produced agricultural products while creating 
                economic opportunities for our community and promoting food security in Nigeria.
              </p>
            </div>
            
            <div className="bg-white p-10 rounded-2xl shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-terracotta/10 rounded-xl">
                  <Eye className="h-8 w-8 text-terracotta" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-stone-900">Our Vision</h3>
              </div>
              <p className="text-stone-600 text-lg leading-relaxed">
                To be the leading sustainable agricultural enterprise in West Africa, known for 
                excellence in quality, innovation in farming practices, and positive community impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
              What We Stand For
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900">
              Our Core Values
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="text-center p-8 bg-stone-50 rounded-2xl hover:bg-stone-100 transition-colors"
                data-testid={`value-${index}`}
              >
                <div className="inline-flex p-4 bg-green-100 rounded-xl mb-6">
                  <value.icon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-stone-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-stone-100" data-testid="team-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
              The People Behind AfriGrow
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900">
              Our Team
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div 
                key={member.name}
                className="bg-white p-8 rounded-2xl shadow-sm text-center"
                data-testid={`team-${index}`}
              >
                <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-12 w-12 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-terracotta font-medium mb-4">{member.role}</p>
                <p className="text-stone-600">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-green-600" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Want to Visit Our Farm?
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            We welcome farm visits! Come see how we raise our livestock and grow our produce. 
            Contact us to schedule a visit.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="schedule-visit-btn"
          >
            <Link to="/contact">
              Schedule a Visit <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
